const asNumber = (value) => parseFloat(value || 0);

const asDisplayString = (value, preferredKeys = []) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    for (const key of preferredKeys) {
      const candidate = value[key];
      if (candidate != null && candidate !== '') return String(candidate);
    }
  }
  return '';
};

const asNullableNumber = (value) => (value == null || value === '' ? null : parseFloat(value));

// One line per tender leg of a completed return (store credit, loyalty points
// given back, money modes, gift vouchers) — see the API's refund_settlement.
const mapSettlementLines = (lines = []) => lines.map((line) => ({
  tenderType: line.tender_type,
  label: line.label || line.tender_type,
  amount: asNumber(line.amount),
  points: asNullableNumber(line.points),
}));

const REFUND_METHOD_LABELS = {
  cash: 'Cash',
  store_credit: 'Store Credit',
  same_payment_mode: 'Same Payment Mode',
};

const buildReturnGstSummary = (items = []) => {
  const rows = new Map();
  items.forEach((item) => {
    const key = `${item.cgst_rate || 0}|${item.sgst_rate || 0}|${item.igst_rate || 0}`;
    const existing = rows.get(key) || {
      label:
        item.igst_rate && asNumber(item.igst_rate) > 0
          ? `IGST ${asNumber(item.igst_rate).toFixed(0)}%`
          : `GST ${(asNumber(item.cgst_rate) + asNumber(item.sgst_rate)).toFixed(0)}%`,
      taxableAmount: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
    };
    const refundAmt = asNumber(item.refund_amount);
    const taxAmt = asNumber(item.tax_amount);
    existing.taxableAmount += Math.max(0, refundAmt - taxAmt);
    existing.cgstAmount += asNumber(item.cgst_amount);
    existing.sgstAmount += asNumber(item.sgst_amount);
    existing.igstAmount += asNumber(item.igst_amount);
    rows.set(key, existing);
  });
  return Array.from(rows.values()).filter(
    (r) => r.taxableAmount > 0 || r.cgstAmount > 0 || r.sgstAmount > 0 || r.igstAmount > 0,
  );
};

export const mapReturnToReceipt = (payload, template) => {
  const ret = payload?.data?.order_return || payload?.order_return || payload;
  if (!ret) return null;

  const isCreditNote = ret.refund_method === 'store_credit' && !!ret.credit_note_number;

  const items = (ret.items || []).map((item, index) => ({
    index: index + 1,
    name: item.product_name || 'Item',
    hsn: asDisplayString(item.hsn_code, ['code', 'value', 'name']),
    quantity: asNumber(item.quantity_returned),
    rate: asNumber(item.unit_price),
    amount: asNumber(item.refund_amount),
    reason: item.reason_name || '',
  }));

  return {
    returnId: ret.id,
    returnNumber: ret.return_number,
    creditNoteNumber: ret.credit_note_number || null,
    invoiceTitle: isCreditNote ? 'CREDIT NOTE' : 'RETURN INVOICE',
    orderNumber: ret.order_number,
    refundMethod: REFUND_METHOD_LABELS[ret.refund_method] || ret.refund_method || '',
    refundAmount: asNumber(ret.refund_amount),
    // Settlement split: cash/credit actually paid out vs loyalty points returned.
    // refundAmount stays the gross (credit-note) total; these break down how it
    // was settled. Falls back to the gross for non-loyalty returns.
    monetaryRefundAmount: asNumber(ret.monetary_refund_amount ?? ret.refund_amount),
    loyaltyPointsRefunded: asNumber(ret.loyalty_points_refunded ?? 0),
    // Completed returns: where the refund actually went, and the customer's
    // store credit / points balance right after it (null when this return
    // didn't move that wallet). Empty for drafts and older API responses.
    settlementLines: mapSettlementLines(ret.refund_settlement),
    storeCreditBalanceAfter: asNullableNumber(ret.store_credit_balance_after),
    loyaltyPointsBalanceAfter: asNullableNumber(ret.loyalty_points_balance_after),
    // BXGY promo discount reversed on kept items (deducted from the refund);
    // the debit note is the GST instrument documenting it.
    promotionClawbackTotal: asNumber(ret.promotion_clawback_total ?? 0),
    promotionClawbackName: (ret.promotion_clawback || [])[0]?.promotion_name || '',
    debitNoteNumber: ret.debit_note_number || null,
    completedAt: ret.completed_at || ret.created_at,
    storeName: ret.store?.name || '',
    storePhone: ret.store?.phone_number || '',
    storeEmail: ret.store?.email || '',
    organizationName: ret.organization?.name || '',
    gstin: ret.organization?.gstin || '',
    customerName: ret.customer?.full_name || '',
    customerMobile: ret.customer?.phone_number || '',
    customerGstin: ret.customer?.gstin || '',
    notes: ret.notes || '',
    items,
    gstSummary: buildReturnGstSummary(ret.items || []),
    terms: template?.terms || [],
    footerMessage: template?.footer_message || '',
    websiteHandle: template?.website_handle || '',
    instagramHandle: template?.instagram_handle || '',
    declarationText: template?.declaration_text || '',
    logoUrl: template?.logo_url || '',
    rateColumnLabel: template?.rate_column_label || 'Rate',
    amountColumnLabel: template?.amount_column_label || 'Amt',
    showRateColumn: template?.show_rate_column !== false,
    showAmountColumn: template?.show_amount_column !== false,
    itemAmountDecimalPlaces: template?.item_amount_decimal_places || 2,
    receiptFontSize: template?.receipt_font_size || 'normal',
  };
};
